import * as RadixDialog from '@radix-ui/react-dialog';
import { dayLabel } from '../dates';
import { formatMoney as money } from '../money';

export interface Preview {
  errors: Record<string, string>;
  visits: {
    date: string;
    start_time: string;
    end_time: string;
    lead_name: string;
    charge: string;
    pay: string;
    holiday: string | null;
  }[];
  summary: {
    charge_per_visit: string;
    pay_per_visit: string;
    margin_per_visit: string;
    visits_per_cycle: number;
    margin_per_cycle: string;
    unstaffed: boolean;
    /** Unstaffed but offered out: pay is the offered rate. */
    offered?: boolean;
    lead_name?: string;
    /** The first day the visits shown are from: on an edit, when the changes apply. */
    from?: string;
  } | null;
}

/**
 * The last look before an assignment goes live: what each visit earns, costs
 * and leaves, and the visits that saving will put on the calendar. Nothing is
 * saved until it is confirmed here.
 */
export function AssignmentReview(props: {
  preview: Preview;
  editing: boolean;
  /** On an edit, what is changing, each as "What: before → after". */
  changes?: string[];
  busy: boolean;
  onConfirm(): void;
  onCancel(): void;
}) {
  const { visits, summary } = props.preview;
  const margin = summary ? Number(summary.margin_per_visit) : 0;
  const cycleMargin = summary ? Number(summary.margin_per_cycle) : 0;
  const totalCharge = visits.reduce((n, v) => n + Number(v.charge), 0);
  const totalPay = visits.reduce((n, v) => n + Number(v.pay), 0);
  const holidays = visits.filter((v) => v.holiday).length;

  return (
    <RadixDialog.Root open onOpenChange={(open) => !open && !props.busy && props.onCancel()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="backdrop">
          <RadixDialog.Content
            className="dialog wide review"
            aria-describedby="review-lede"
            onInteractOutside={(e) => e.preventDefault()}
          >
            <RadixDialog.Title asChild>
              <h2>{props.editing ? 'Check the changes' : 'Check before creating'}</h2>
            </RadixDialog.Title>
            <RadixDialog.Description id="review-lede" className="muted">
              {props.editing
                ? `From ${summary?.from ? dayLabel(summary.from) : 'the change date'}, visits follow what is below. Visits before then stay as booked.`
                : visits.length > 0
                  ? 'These visits go on the calendar as soon as you create the assignment.'
                  : 'No visits fall in this range yet. The assignment is saved and makes them once its dates arrive.'}
            </RadixDialog.Description>

            {props.editing && props.changes && (
              <section className="review-changes" aria-labelledby="review-changes-h">
                <h3 id="review-changes-h">What changes</h3>
                {props.changes.length > 0 ? (
                  <ul>
                    {props.changes.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">Nothing that affects visits, rates or the customer.</p>
                )}
              </section>
            )}

            <dl className="review-money">
              <div>
                <dt>Revenue per visit</dt>
                <dd>{summary ? money(summary.charge_per_visit) : '—'}</dd>
              </div>
              <div>
                <dt>Contractor pay per visit</dt>
                <dd>{summary ? money(summary.pay_per_visit) : '—'}</dd>
              </div>
              <div className={margin < 0 ? 'review-margin neg' : 'review-margin'}>
                <dt>Margin per visit</dt>
                <dd>{summary ? money(summary.margin_per_visit) : '—'}</dd>
              </div>
            </dl>
            {summary && (
              <p className="review-cycle">
                Per cycle of {summary.visits_per_cycle}{' '}
                {summary.visits_per_cycle === 1 ? 'visit' : 'visits'}, the margin is{' '}
                <strong className={cycleMargin < 0 ? 'neg' : ''}>
                  {money(summary.margin_per_cycle)}
                </strong>
                .
              </p>
            )}
            {margin < 0 && (
              <p className="review-warning" role="alert">
                Each visit costs more in pay than it charges. Check the rates before going ahead.
              </p>
            )}
            {summary?.unstaffed && (
              <p className="review-warning">
                {summary.offered
                  ? 'No lead contractor yet. Pay is worked out at the rate offered to contractors, for whoever claims it.'
                  : 'No lead contractor yet, so these visits start unassigned.'}
              </p>
            )}

            {visits.length > 0 && (
              <>
                <h3 className="review-list-head">
                  {props.editing ? 'Next' : 'First'} {visits.length}{' '}
                  {visits.length === 1 ? 'visit' : 'visits'}
                  {holidays > 0 && (
                    <span className="pill warn">{holidays} on a public holiday</span>
                  )}
                </h3>
                <div className="review-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Contractor</th>
                        <th className="num">Charge</th>
                        <th className="num">Pay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visits.map((v) => (
                        <tr key={`${v.date}-${v.start_time}`}>
                          <td>
                            {dayLabel(v.date)}
                            {v.holiday && (
                              <span className="pill warn"> Public holiday: {v.holiday}</span>
                            )}
                          </td>
                          <td>
                            {v.start_time}–{v.end_time}
                          </td>
                          <td>{v.lead_name || 'Unassigned'}</td>
                          <td className="num">{money(v.charge)}</td>
                          <td className="num">{money(v.pay)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th scope="row" colSpan={3}>
                          Total for these {visits.length}
                        </th>
                        <td className="num">{money(String(totalCharge))}</td>
                        <td className="num">{money(String(totalPay))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}

            <div className="row end">
              <button
                type="button"
                className="quiet"
                disabled={props.busy}
                onClick={props.onCancel}
              >
                Back to editing
              </button>
              <button type="button" disabled={props.busy} onClick={props.onConfirm}>
                {props.busy
                  ? 'Saving…'
                  : props.editing
                    ? 'Save changes'
                    : visits.length > 0
                      ? 'Create assignment and visits'
                      : 'Create assignment'}
              </button>
            </div>
          </RadixDialog.Content>
        </RadixDialog.Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
